import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import type { PushPermission } from '@/lib/push';

import { useDevicesDeps } from './context';
import type { Device } from './schemas';

/**
 * The TanStack Query layer for BP-11 §6's notifications screen.
 *
 * **Nothing polls.** A device list changes when this person installs,
 * uninstalls or logs out — all of which happen on this screen or on this
 * handset — and rule R6's disabling is a thing the *server* learns, which
 * the next reminder will show. A poll would be a request every few seconds
 * for a list that changes a few times a year.
 */

/** Query keys, namespaced so a sign-out cannot leave the last person's phones. */
export const deviceKeys = {
  all: ['devices'] as const,
  mine: () => [...deviceKeys.all, 'mine'] as const,
  permission: () => [...deviceKeys.all, 'permission'] as const,
};

/** §6's `DeviceList` — every phone of the signed-in client, newest first. */
export function useMyDevices(): UseQueryResult<Device[]> {
  const { api } = useDevicesDeps();
  return useQuery({ queryKey: deviceKeys.mine(), queryFn: () => api.listDevices() });
}

/** §6's `PushToggle` reads this to decide what to draw (R11's four states). */
export function usePushPermission(): UseQueryResult<PushPermission> {
  const { permission } = useDevicesDeps();
  return useQuery({ queryKey: deviceKeys.permission(), queryFn: () => permission() });
}

/**
 * §6's "enable push": the OS prompt, then whatever it answered.
 *
 * The device row is *not* created here. The token arrives on the plugin's
 * `registration` listener — after this resolves, and again on every
 * rotation — and `useRegisterDevice` is what posts it. Collapsing the two
 * would mean a screen that showed "on" before FCM had answered.
 */
export function useEnablePush(): UseMutationResult<PushPermission, unknown, void> {
  const { enable } = useDevicesDeps();
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => enable(),
    onSuccess: (state) => {
      client.setQueryData(deviceKeys.permission(), state);
      void client.invalidateQueries({ queryKey: deviceKeys.mine() });
    },
  });
}

/** R4: post a token the OS just handed over, and show the row it made. */
export function useRegisterDevice(): UseMutationResult<
  Device,
  unknown,
  { platform: 'android' | 'ios'; token: string; app_version?: string; locale?: string }
> {
  const { api } = useDevicesDeps();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (registration) => api.registerDevice(registration),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: deviceKeys.mine() });
    },
  });
}

/**
 * §6's "forget this phone", and R4's logout.
 *
 * The OS is told first and the row deleted second. Either order leaves a
 * window, and this is the safe one: a row that outlives the unregister is a
 * reminder that goes nowhere, while an unregister that outlives the row is
 * a salon still counting a device it can no longer reach.
 */
export function useForgetDevice(): UseMutationResult<void, unknown, string> {
  const { api, disable } = useDevicesDeps();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await disable();
      await api.forgetDevice(id);
    },
    onSuccess: () => {
      client.setQueryData(deviceKeys.permission(), 'prompt');
      void client.invalidateQueries({ queryKey: deviceKeys.mine() });
    },
  });
}
